import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import userManager, { isLoggedOutLanding } from "../services/oidc";
import tokenService from "../services/tokenService";

interface OIDCUser {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_at: number;
  profile: {
    sub: string;
    preferred_username?: string;
    email?: string;
    name?: string;
    given_name?: string;
    family_name?: string;
    iss?: string;
  };
}

interface OIDCContextType {
  user: OIDCUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  error: string | null;
}

const OIDCContext = createContext<OIDCContextType | undefined>(undefined);

export const useOIDC = (): OIDCContextType => {
  const context = useContext(OIDCContext);
  if (!context) {
    throw new Error("useOIDC must be used within an OIDCProvider");
  }
  return context;
};

interface OIDCProviderProps {
  children: ReactNode;
}

/** Sync React auth state with localStorage; refresh Keycloak if access token expired. */
const syncAuthFromStorage = async (): Promise<OIDCUser | null> => {
  const userData = await userManager.getUser();
  return userData;
};

export const OIDCProvider: React.FC<OIDCProviderProps> = ({ children }) => {
  const [user, setUser] = useState<OIDCUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applySession = (userData: OIDCUser | null) => {
    if (userData) {
      setIsAuthenticated(true);
      setUser(userData);
    } else {
      tokenService.clearStoredToken();
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  useEffect(() => {
    // Check if we're on the callback route
    const publicUrl = process.env.PUBLIC_URL || "";
    const callbackPath = publicUrl + "/callback";

    console.log(
      "OIDCProvider useEffect - Current pathname:",
      window.location.pathname
    );
    console.log("OIDCProvider useEffect - Current URL:", window.location.href);
    console.log(
      "OIDCProvider useEffect - Expected callback path:",
      callbackPath
    );

    // Check for authorization code in URL (indicates callback)
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = urlParams.get("code");

    if (window.location.pathname === callbackPath || hasCode) {
      console.log(
        "Detected callback route or authorization code, skipping authentication check"
      );
      console.log("Has authorization code:", hasCode ? "Yes" : "No");
      setIsLoading(false);
      return;
    }

    // Post-logout landing: session already cleared — AuthWrapper will send user to login
    if (isLoggedOutLanding()) {
      console.log("Post-logout landing detected, preparing login redirect");
      tokenService.clearStoredToken();
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
      return;
    }

    // Helper: token is valid only if not expired (expires_at is in seconds; allow 30s buffer)
    const isTokenExpired = (expiresAt: number) =>
      !expiresAt || expiresAt <= Math.floor(Date.now() / 1000) + 30;

    // Only clear structurally invalid storage — expired access tokens are refreshed via getUser()
    const clearInvalidStorage = () => {
      const userStr = localStorage.getItem("oidc.user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const missing =
            !user.access_token || !user.expires_at || !user.profile;
          if (missing) {
            console.log("Clearing user data: missing required fields");
            localStorage.removeItem("oidc.user");
            localStorage.removeItem("processed_code");
            localStorage.removeItem("oidc.state");
            tokenService.clearStoredToken();
          }
        } catch (error) {
          console.log("Clearing invalid user data");
          localStorage.removeItem("oidc.user");
          localStorage.removeItem("processed_code");
          localStorage.removeItem("oidc.state");
        }
      }
    };

    clearInvalidStorage();

    // Check if we just came back from a successful callback (only trust non-expired token)
    const processedCode = localStorage.getItem("processed_code");
    if (processedCode) {
      console.log("Detected processed callback, checking for user data...");
      const userStr = localStorage.getItem("oidc.user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const valid =
            user.access_token &&
            user.expires_at &&
            user.profile &&
            !isTokenExpired(user.expires_at);
          if (valid) {
            console.log(
              "Found valid user data from callback, setting as authenticated"
            );
            setIsAuthenticated(true);
            setUser(user);
            setIsLoading(false);
            return;
          } else if (
            user.access_token &&
            user.expires_at &&
            isTokenExpired(user.expires_at)
          ) {
            console.log(
              "Stored access token expired, will attempt refresh via getUser()"
            );
          } else {
            console.warn("User data from callback missing required fields:", {
              hasAccessToken: !!user.access_token,
              hasExpiresAt: !!user.expires_at,
              hasProfile: !!user.profile
            });
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
        }
      } else {
        console.warn("Processed code found but no user data in localStorage");
        setTimeout(() => {
          const retryUserStr = localStorage.getItem("oidc.user");
          if (retryUserStr) {
            try {
              const user = JSON.parse(retryUserStr);
              const valid =
                user.access_token &&
                user.expires_at &&
                user.profile &&
                !isTokenExpired(user.expires_at);
              if (valid) {
                console.log("Found user data on retry, setting as authenticated");
                setIsAuthenticated(true);
                setUser(user);
                setIsLoading(false);
              }
            } catch (error) {
              console.error("Error parsing user data on retry:", error);
            }
          }
        }, 200);
      }
    }

    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.log(
        "OIDC initialization timeout, proceeding without authentication"
      );
      console.log("This means the login button should now appear");
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
    }, 3000);

    // Check authentication status
    console.log("=== OIDC PROVIDER AUTHENTICATION CHECK ===");
    console.log("Checking authentication status...");
    console.log("Current pathname:", window.location.pathname);
    console.log("Current URL:", window.location.href);

    syncAuthFromStorage()
      .then((userData) => {
        console.log("User from OIDC:", userData);
        clearTimeout(timeoutId);
        applySession(userData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error checking authentication:", error);
        clearTimeout(timeoutId);
        applySession(null);
        setIsLoading(false);
      });

    return () => clearTimeout(timeoutId);
  }, []);

  // Listen for custom authentication events
  useEffect(() => {
    const handleAuthUpdate = () => {
      console.log(
        "Authentication update event received, re-syncing session"
      );
      syncAuthFromStorage().then(applySession);
    };

    const handleAuthLogout = () => {
      console.log("OIDC logout event received in provider, clearing state");
      applySession(null);
    };

    window.addEventListener("oidc-auth-update", handleAuthUpdate);
    window.addEventListener("oidc-auth-logout", handleAuthLogout);
    return () => {
      window.removeEventListener("oidc-auth-update", handleAuthUpdate);
      window.removeEventListener("oidc-auth-logout", handleAuthLogout);
    };
  }, []);

  // User returned to tab/app after idle — try silent Keycloak refresh, then update UI
  useEffect(() => {
    const handleReturnToApp = () => {
      if (document.visibilityState === "hidden") {
        return;
      }
      syncAuthFromStorage().then(applySession);
    };

    window.addEventListener("focus", handleReturnToApp);
    document.addEventListener("visibilitychange", handleReturnToApp);
    return () => {
      window.removeEventListener("focus", handleReturnToApp);
      document.removeEventListener("visibilitychange", handleReturnToApp);
    };
  }, []);

  const login = useCallback(() => {
    console.log("=== OIDC LOGIN CALLED ===");
    console.log("Attempting to login with OIDC...");
    setError(null);

    try {
      console.log("Calling userManager.signinRedirect()...");
      userManager.signinRedirect();
      console.log("userManager.signinRedirect() called successfully");
    } catch (error) {
      console.error("Login failed:", error);
      setError("Login failed: " + (error as Error).message);
    }
  }, []);

  const logout = useCallback(() => {
    console.log("=== OIDC LOGOUT CALLED ===");
    try {
      // Do not clear oidc.user / React state here — that races MapView.destroy()
      // and drops id_token before Keycloak logout. signoutRedirect handles it.
      userManager.signoutRedirect();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, []);

  const value: OIDCContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    error,
  };

  return <OIDCContext.Provider value={value}>{children}</OIDCContext.Provider>;
};

export default OIDCProvider;
